/**
 * Represents parsed server-sent events.
 * Simpler than using a MessageEvent object.
 */
export interface SseEvent {
  type: string;
  data: string;
}

/**
 * When a stream is parsed, a data buffer, an event type buffer, and a last
 * event ID buffer must be associated with it. They must be initialized to the
 * empty string.
 *
 * For Corteva Chat, we don't need or use lastEventId. Also `dispatchedEvents`
 * has been added to collect parsed events.
 */
interface SseParserState {
  dataBuffer: string;
  eventTypeBuffer: string;
  lastEventIdBuffer: string;
  dispatchedEvents: SseEvent[];
}

/**
 * Like String.split(), but splits on the first occurence of a given character
 * rather than on all of them.
 * e.g.
 * "data: Good news: everyone!" ->
 *  ["data", "Good news: everyone!"]
 */
function splitOnFirstOccurence(
  line: string,
  character: string
): [string, string] {
  const firstOccurenceIndex = line.indexOf(character);
  if (firstOccurenceIndex !== -1) {
    const firstPart = line.substring(0, firstOccurenceIndex);
    const secondPart = line.substring(firstOccurenceIndex + 1);
    return [firstPart, secondPart];
  }
  return ["", ""];
}

/**
 * Implements relevant parts of the SSE spec's dispatchMessage procedure.
 * https://html.spec.whatwg.org/multipage/server-sent-events.html#dispatchMessage
 */
function _dispatchEvent(state: SseParserState) {
  // If the data buffer is an empty string,
  if (state.dataBuffer === "") {
    // set the data buffer and the event type buffer to the empty string and
    // return.
    state.dataBuffer = "";
    state.eventTypeBuffer = "";
    return;
  }

  // If the data buffer's last character is a U+000A LINE FEED (LF) character,
  if (state.dataBuffer.endsWith("\n")) {
    // then remove the last character from the data buffer.
    state.dataBuffer = state.dataBuffer.slice(0, -1);
  }

  // If the event type buffer has a value other than the empty string, change
  // the type of the newly created event to equal the value of the event type
  // buffer.
  let eventType = "message";
  if (state.eventTypeBuffer !== "") {
    eventType = state.eventTypeBuffer;
  }
  // Unlike the spec, we use a simple object instead of a MessageEvent.
  // The main reason is that MessageEvent's show up in the console like:
  //  MessageEvent { "isTrusted": false, }
  // Not as nice as:
  //  {type: "message", data: "wow I can actually read the data!"}
  const event = {
    type: eventType,
    data: state.dataBuffer,
  };

  // Set the data buffer and the event type buffer to the empty string.
  state.dataBuffer = "";
  state.eventTypeBuffer = "";

  state.dispatchedEvents.push(event);
}

/**
 * Implements the SSE spec's process field procedure.
 * https://html.spec.whatwg.org/multipage/server-sent-events.html#processField
 */
function _processField(state: SseParserState, field: string, value: string) {
  // If the field name is "event",
  if (field === "event") {
    // set the event type buffer to the field value.
    state.eventTypeBuffer = value;
  }
  // If the field name is "data",
  else if (field === "data") {
    // append the field value to the data buffer, then append a single U+000A
    // LINE FEED (LF) character to the data buffer.
    state.dataBuffer = state.dataBuffer + value + "\n";
  }
  // If the field name is "id".
  else if (field === "id") {
    // If the field value does not contain U+0000 NULL, then set the last event
    // ID buffer to the field value. Otherwise, ignore the field.
    if (!value.includes("\0")) {
      state.lastEventIdBuffer = value;
    }
  }
  // If the field name is "retry",
  else if (field === "retry") {
    // ignore it.
  } else {
    // ignore it.
  }
}

/**
 * Processes one line from an SSE stream.
 */
function _processLine(state: SseParserState, line: string) {
  // If the line is empty (a blank line)
  if (line === "") {
    // Dispatch the event.
    _dispatchEvent(state);
  }
  // If the line starts with a U+003A COLON character (:)
  else if (line[0] === ":") {
    // Ignore the line.
  }
  // If the line contains a U+003A COLON character (:)
  else if (line.includes(":")) {
    // Collect the characters on the line before the first U+003A COLON character
    // (:), and let field be that string.
    // Collect the characters on the line after the first U+003A COLON character
    // (:), and let value be that string.
    // e.g. `data: hello` -> ["data", "hello"]
    let [field, value] = splitOnFirstOccurence(line, ":");
    // If value starts with a U+0020 SPACE character, remove it from value.
    if (value[0] === " ") {
      value = value.slice(1);
    }
    // Process the field using the steps described below, using field as the
    // field name and value as the field value.
    _processField(state, field, value);
  }
  // Otherwise, the string is not empty but does not contain a U+003A COLON
  // character (:)
  else {
    // Process the field using the steps described below, using the whole line
    // as the field name, and the empty string as the field value.
    let field = line;
    let value = "";
    _processField(state, field, value);
  }
}

/**
 * Parses server-sent events.
 * https://html.spec.whatwg.org/multipage/server-sent-events.html#event-stream-interpretation
 */
export function sseParser(xhrResponseText: string) {
  // Strip BOM (Byte Order Mark) from the start of the stream if present.
  // The spec requires UTF-8 encoding, and the BOM should be ignored.
  if (xhrResponseText.startsWith("\uFEFF")) {
    xhrResponseText = xhrResponseText.slice(1);
  }

  // When a stream is parsed, a data buffer, an event type buffer, and a last
  // event ID buffer must be associated with it. They must be initialized to
  // the empty string.
  let state: SseParserState = {
    dataBuffer: "",
    eventTypeBuffer: "",
    lastEventIdBuffer: "",
    dispatchedEvents: [],
  };

  // Lines must be processed, in the order they are received, as follows:
  let lines = xhrResponseText.split(/\r\n|\r|\n/);

  for (let line of lines) {
    _processLine(state, line);
  }

  return state.dispatchedEvents;
}
